import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/logo.jpeg";

/* ICONS */
import dashboardIcon from "../assets/dashboard-icon.svg";
import cashIcon from "../assets/cash-in-hand-icon.png";
import inventoryIcon from "../assets/inventory-icon.png";
import customersIcon from "../assets/customer-icon.png";
import salesIcon from "../assets/transaction-icon.png";
import reportsIcon from "../assets/reports-icon.png";
import rolesIcon from "../assets/manage-roles-icon.png";
import companyIcon from "../assets/company-profile-icon.png";
import expenseIcon from "../assets/expense-icon.png";

const COLORS = {
  primary: "#0D47A1",
  red: "#D32F2F",
  yellow: "#FBC02D",
  bgCream: "#FFF6EA",
  textDark: "#1E1E1E",
  textGrey: "#757575",
  sidebarBg: "#FFFFFF",
  border: "#E5E7EB",
  activeBg: "#E3F2FD",
};

const DESKTOP_SIDEBAR = 280;
const MOBILE_SIDEBAR = 240;

const MainLayout = ({ children }) => {
  const { user, role, logout } = useAuth();
  const navigate = useNavigate();

  const [openInventory, setOpenInventory] = useState(false);
  const [openTxn, setOpenTxn] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreen = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  const toggleSidebar = () => {
    if (isMobile) setMobileOpen(!mobileOpen);
    else setSidebarOpen(!sidebarOpen);
  };

  const closeAll = () => {
    setOpenInventory(false);
    setOpenTxn(false);
    if (isMobile) setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const userName = user?.username || "User";
  const userRole = role || "N/A";

  return (
    <div style={styles.wrapper}>
      {/* MOBILE HEADER */}
      {isMobile && (
        <div style={styles.mobileHeader}>
          <button style={styles.menuBtn} onClick={toggleSidebar}>
            ☰
          </button>
          <div style={styles.mobileTitle}>Khata Saarthi</div>
        </div>
      )}

      {/* OVERLAY */}
      {isMobile && mobileOpen && (
        <div style={styles.overlay} onClick={() => setMobileOpen(false)} />
      )}

      {/* SIDEBAR */}
      <div
        style={{
          ...styles.sidebar,
          width: isMobile ? MOBILE_SIDEBAR : DESKTOP_SIDEBAR,
          top: isMobile ? 60 : 0,
          transform: isMobile
            ? mobileOpen
              ? "translateX(0)"
              : "translateX(-100%)"
            : sidebarOpen
            ? "translateX(0)"
            : "translateX(-100%)",
        }}
      >
        {/* Menu Content Wrapper (Scrollable Area) */}
        <div style={styles.menuScrollArea}>
          <div style={styles.logoWrap}>
            <img src={logo} alt="Logo" style={styles.logoImg} />
            <div>
              <div style={styles.logoText}>Khata Saarthi</div>
              <div style={styles.logoSub}>Smart Business Manager</div>
            </div>
          </div>

          <div style={styles.userCard}>
            <div style={styles.avatar}>{userName.charAt(0)}</div>
            <div>
              <div style={styles.userName}>{userName}</div>
              <div style={styles.roleBadge}>{userRole}</div>
            </div>
          </div>

          <Section title="MAIN" />
          <MenuItem to="/" label="Dashboard" icon={dashboardIcon} onClick={closeAll} />
          <MenuItem to="/cash" label="Cash In Hand" icon={cashIcon} onClick={closeAll} />

          <Section title="INVENTORY" />
          <Parent
            label="Inventory"
            icon={inventoryIcon}
            open={openInventory}
            onClick={() => {
              setOpenInventory(!openInventory);
              setOpenTxn(false);
            }}
          />
          <SubWrap open={openInventory}>
            <SubItem to="/inventory" label="Products" onClick={closeAll} />
            <SubItem to="/inventory/units" label="Units" onClick={closeAll} />
            <SubItem to="/inventory/types" label="Product Types" onClick={closeAll} />
          </SubWrap>

          <Section title="PARTIES" />
          <MenuItem to="/customers" label="Parties" icon={customersIcon} onClick={closeAll} />

          <Section title="TRANSACTIONS" />
          <Parent
            label="Transactions"
            icon={salesIcon}
            open={openTxn}
            onClick={() => {
              setOpenTxn(!openTxn);
              setOpenInventory(false);
            }}
          />
          <SubWrap open={openTxn}>
            <SubItem to="/sales" label="Sales" onClick={closeAll} />
            <SubItem to="/salereturn" label="Sales Return" onClick={closeAll} />
            <SubItem to="/purchase" label="Purchase" onClick={closeAll} />
            <SubItem to="/purchasereturn" label="Purchase Return" onClick={closeAll} />
            <SubItem to="/payment-in" label="Payment In" onClick={closeAll} />
            <SubItem to="/payment-out" label="Payment Out" onClick={closeAll} />
            <SubItem to="/daybook" label="Daybook" onClick={closeAll} />
            <SubItem to="/ledger" label="Ledger" onClick={closeAll} />
          </SubWrap>

          <Section title="EXPENSES" />
          <MenuItem to="/expense" label="Expense" icon={expenseIcon} onClick={closeAll} />

          <Section title="REPORTS" />
          <MenuItem to="/reports" label="Reports" icon={reportsIcon} onClick={closeAll} />

          <Section title="ROLES" />
          <MenuItem to="/manageroles" label="Manage Roles" icon={rolesIcon} onClick={closeAll} />
        </div>

        {/* BOTTOM SECTION (Fixed at bottom of sidebar) */}
        <div style={styles.bottomSection}>
          <button style={styles.logoutBtnFull} onClick={handleLogout}>
            Logout
          </button>
          <div style={styles.footer}>
            <div>All rights reserved by</div>
            <a href="https://codesaarthi.com" target="_blank" rel="noreferrer" style={styles.link}>
              CodeSaarthi
            </a>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div
        style={{
          ...styles.main,
          marginLeft: !isMobile && sidebarOpen ? DESKTOP_SIDEBAR : 0,
          marginTop: isMobile ? 60 : 0,
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* COMPONENTS */
const Section = ({ title }) => <div style={styles.section}>{title}</div>;

const MenuItem = ({ to, label, icon, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    style={({ isActive }) => ({
      ...styles.menuItem,
      background: isActive ? COLORS.activeBg : "transparent",
      color: isActive ? COLORS.primary : COLORS.textDark,
    })}
  >
    <div style={styles.itemLeft}>
      <img src={icon} alt="" style={styles.icon} />
      {label}
    </div>
  </NavLink>
);

const Parent = ({ label, icon, open, onClick }) => (
  <div style={styles.parent} onClick={onClick}>
    <div style={styles.itemLeft}>
      <img src={icon} alt="" style={styles.icon} />
      {label}
    </div>
    <span style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "0.2s" }}>&gt;</span>
  </div>
);

const SubWrap = ({ open, children }) => (
  <div style={{ ...styles.subWrap, maxHeight: open ? 500 : 0 }}>{children}</div>
);

const SubItem = ({ to, label, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    style={({ isActive }) => ({
      ...styles.subItem,
      background: isActive ? COLORS.activeBg : "transparent",
      color: isActive ? COLORS.primary : COLORS.textGrey,
    })}
  >
    {label}
  </NavLink>
);

/* STYLES */
const styles = {
  wrapper: { display: "flex", minHeight: "100vh", background: COLORS.bgCream },

  mobileHeader: {
    height: 60,
    background: "#fff",
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    borderBottom: `1px solid ${COLORS.border}`,
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1100,
    boxSizing: "border-box",
  },

  menuBtn: {
    fontSize: 22,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    marginRight: 12,
  },

  mobileTitle: { fontWeight: 600, color: COLORS.primary },

  sidebar: {
    position: "fixed",
    left: 0,
    bottom: 0,
    background: COLORS.sidebarBg,
    borderRight: `1px solid ${COLORS.border}`,
    zIndex: 1000,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.3s ease",
  },

  menuScrollArea: {
    flex: 1,
    overflowY: "auto",
    padding: "20px 16px 10px 16px",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    zIndex: 900,
  },

  main: {
    flex: 1,
    width: "100%",
    padding: "16px",
    boxSizing: "border-box",
    transition: "margin 0.3s ease",
  },

  bottomSection: {
    padding: "16px",
    borderTop: `1px solid ${COLORS.border}`,
    background: COLORS.sidebarBg,
    flexShrink: 0,
  },

  logoutBtnFull: {
    width: "100%",
    padding: "12px",
    borderRadius: 10,
    border: `2px solid ${COLORS.red}`,
    background: "transparent",
    color: COLORS.red,
    fontWeight: 600,
    cursor: "pointer",
    marginBottom: 8,
  },

  footer: { fontSize: 11, textAlign: "center", color: COLORS.textGrey },
  link: { color: COLORS.primary, textDecoration: "none", fontWeight: 600 },
  logoWrap: { display: "flex", alignItems: "center", gap: 14, marginBottom: 24 },
  logoImg: { width: 40, height: 40, borderRadius: 8 },
  logoText: { fontSize: 18, fontWeight: 700, color: COLORS.primary },
  logoSub: { fontSize: 12, color: COLORS.textGrey },

  userCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px",
    borderRadius: 12,
    background: COLORS.bgCream,
    marginBottom: 20,
  },

  avatar: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: COLORS.primary,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
  },

  userName: { fontSize: 14, fontWeight: 600 },
  roleBadge: { fontSize: 11, color: COLORS.red, fontWeight: 600 },
  section: { fontSize: 11, fontWeight: 700, color: COLORS.textGrey, margin: "18px 0 8px" },
  menuItem: { padding: "12px 14px", borderRadius: 8, textDecoration: "none", display: "block", marginBottom: 4 },
  parent: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", cursor: "pointer" },
  subWrap: { overflow: "hidden", transition: "all 0.3s ease" },
  subItem: { padding: "10px 20px", borderRadius: 8, textDecoration: "none", display: "block", fontSize: 14, marginLeft: 10 },
  itemLeft: { display: "flex", alignItems: "center", gap: 12 },
  icon: { width: 18, height: 18 },
};

export default MainLayout;